import { forwardRef } from 'react';
import cx from 'classnames';
import './BrandLogo.css';

/**
 * QHSLab brand logo — single source of truth.
 *
 * Variants:
 *   - 'wordmark' (default) — full QHSLab wordmark + checkmark glyph (logo lockup)
 *   - 'glyph' — checkmark-in-circle only, square aspect, scales to size prop
 *
 * Props:
 *   - variant: 'wordmark' | 'glyph'
 *   - tone:    'inherit' (currentColor) | 'white' | 'brand'
 *   - size:    number — sets explicit width in px (height auto-scales)
 *   - animated: bool — pulse the two glyph parts (used inside boot loader)
 */
const BrandLogo = forwardRef(function BrandLogo(
  { variant = 'wordmark', tone = 'inherit', size, animated = false, className, ...rest },
  ref
) {
  const fill =
    tone === 'white' ? '#ffffff' : tone === 'brand' ? 'var(--qhs-brand)' : 'currentColor';

  const styleProp = size ? { width: size, height: 'auto' } : undefined;
  const classes = cx('qhs-logo', `qhs-logo--${variant}`, animated && 'qhs-logo--animated', className);

  if (variant === 'glyph') {
    return (
      <svg
        ref={ref}
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        className={classes}
        style={styleProp}
        aria-hidden="true"
        focusable="false"
        {...rest}
      >
        <path
          className="qhs-logo__part qhs-logo__part-1"
          fill={fill}
          d="M30.87,49.74A18.87,18.87,0,1,1,49.74,30.87c0,.34,0,.66-.05,1L52,34.14l9.2-9.24a30.85,30.85,0,1,0-19.71,35L31.33,49.72Z"
        />
        <polygon
          className="qhs-logo__part qhs-logo__part-2"
          fill={fill}
          points="81.47,22.8 76.34,17.66 61.71,32.35 50.96,43.14 47.54,39.7 38.54,30.67 34.42,34.81 29.44,39.81 30.43,40.81 37.95,48.36 46.85,57.3 50.96,61.42 81.46,30.8 85.45,26.79 81.47,22.8"
        />
      </svg>
    );
  }

  return (
    <svg
      ref={ref}
      viewBox="0 0 341.26 61.74"
      xmlns="http://www.w3.org/2000/svg"
      className={classes}
      style={styleProp}
      role="img"
      aria-label="QHSLab"
      {...rest}
    >
      <g fill={fill}>
        <path
          className="qhs-logo__part qhs-logo__part-1"
          d="M30.87,49.74A18.87,18.87,0,1,1,49.74,30.87c0,.34,0,.66-.05,1L52,34.14l9.2-9.24a30.85,30.85,0,1,0-19.71,35L31.33,49.72Z"
        />
        <polygon
          className="qhs-logo__part qhs-logo__part-2"
          points="81.47 22.8 76.34 17.66 61.71 32.35 50.96 43.14 47.54 39.7 38.54 30.67 34.42 34.81 29.44 39.81 30.43 40.81 37.95 48.36 46.85 57.3 50.96 61.42 81.46 30.8 85.45 26.79 81.47 22.8"
        />
        <path d="M245.53,48.85h23V54H238.94V5.36h6.59Z" />
        <path d="M296.09,41.5h-20.4L271.08,54h-6.72L283.18,5.36h5.65L307.35,54h-6.72ZM277.73,36h16.38l-8-22.16h-.2Z" />
        <path d="M307.19,54V5.36h15.88c5.09,0,9.06,1.09,11.94,3.29s4.31,5.49,4.31,9.88a9,9,0,0,1-2.07,5.8,12.19,12.19,0,0,1-5.45,3.79,11.29,11.29,0,0,1,6.94,4.32,12.78,12.78,0,0,1,2.52,7.86q0,6.68-4.33,10.21T325.18,54Zm6.59-28h10.66a8.74,8.74,0,0,0,6-2,7.1,7.1,0,0,0,2.31-5.63,7.07,7.07,0,0,0-2.49-5.91c-1.66-1.3-4.06-2-7.18-2h-9.29Zm0,5.18V48.85h11.4a10.18,10.18,0,0,0,7-2.21,8,8,0,0,0,2.51-6.28,9.94,9.94,0,0,0-2.09-6.69c-1.4-1.62-3.54-2.44-6.44-2.44Z" />
        <path d="M144.67,55.48l-5.28-4.73a26.13,26.13,0,0,1-14.24,4.11c-14.79,0-25.41-11-25.41-24.65v-.14c0-13.63,10.76-24.79,25.55-24.79s25.4,11,25.4,24.65v.14a24.57,24.57,0,0,1-4.24,13.76L151.38,48ZM131.59,43.83l-7.81-6.64,6.71-7.53,7.88,7.12a16.84,16.84,0,0,0,1.3-6.57v-.14c0-8.22-6-15.07-14.52-15.07s-14.38,6.72-14.38,14.93v.14c0,8.22,6,15.06,14.52,15.06A14.44,14.44,0,0,0,131.59,43.83Z" />
        <path d="M153.93,6.1h10.55v19h19.45V6.1h10.54V54H183.93V34.79H164.48V54H153.93Z" />
        <path d="M197.55,47.05l6.23-7.46c4.32,3.56,8.84,5.82,14.31,5.82,4.32,0,6.92-1.71,6.92-4.52v-.14c0-2.67-1.64-4-9.66-6.09-9.65-2.47-15.88-5.14-15.88-14.66v-.13c0-8.7,7-14.45,16.78-14.45a27.76,27.76,0,0,1,17.8,6.09l-5.48,8c-4.24-2.95-8.42-4.73-12.46-4.73S210,16.58,210,18.91V19c0,3.16,2.05,4.18,10.34,6.3,9.72,2.54,15.2,6,15.2,14.39v.13c0,9.52-7.26,14.86-17.6,14.86A30.48,30.48,0,0,1,197.55,47.05Z" />
      </g>
    </svg>
  );
});

export default BrandLogo;
